import { useMemo } from 'react';

import type { TranslationPath, TranslationRecord, TranslationValue } from './types';
import { useLocaleContext } from './LocaleContext';

const PATH_SEPARATOR = '.';

const isRecord = (value: TranslationValue): value is TranslationRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export type TranslateFn = <T extends TranslationValue = TranslationValue>(
  path: string | TranslationPath<T>,
  fallback?: T,
) => T;

export function resolveTranslation<T extends TranslationValue = TranslationValue>(
  dictionary: TranslationRecord,
  path: string | TranslationPath<T>,
  fallback?: T,
): T {
  const segments = Array.isArray(path) ? path : (path as string).split(PATH_SEPARATOR);
  let current: TranslationValue = dictionary;

  for (const segment of segments) {
    if (!segment) {
      continue;
    }

    if (isRecord(current) && segment in current) {
      current = current[segment];
    } else {
      return fallback as T;
    }
  }

  return (current as T) ?? (fallback as T);
}

export function useTranslations() {
  const { locale, dictionary, setLocale } = useLocaleContext();

  const translator = useMemo<TranslateFn>(
    () =>
      (path, fallback) =>
        resolveTranslation(dictionary, path, fallback),
    [dictionary],
  );

  return { locale, t: translator, dictionary, setLocale } as const;
}
